import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Page, Reveal, MaskedLine, Eyebrow } from "@/components/motion/Reveal";
import SpeakingInquiryForm from "@/components/SpeakingInquiryForm";
import { IMG } from "@/constants/images";

const PRO_TOPICS = [
  { title: "The Beginning of What's Next", desc: "Purpose, leadership, resilience, reinvention, and recognizing when there is more ahead." },
  { title: "Discover Your Purpose. Develop Your Potential.", desc: "Clarity, confidence, and intentional growth." },
  { title: "Build the Leader Before You Build the Business", desc: "Leadership as the foundation of entrepreneurship." },
  { title: "From Vision to Execution", desc: "Turning ideas into strategy, systems, and action." },
  { title: "Resilient Leadership", desc: "Developing leaders and teams that navigate change well." },
  { title: "Women Who Lead", desc: "Purpose, ambition, leadership, growth, and meaningful impact." },
];

const YOUTH_TOPICS = [
  { title: "Discover Your Potential", desc: "Helping young people recognize their strengths, possibilities, and the potential already within them." },
  { title: "Your Future Starts Now", desc: "Encouraging students to understand that the choices, habits, confidence, and leadership they develop today can help shape their future." },
  { title: "The Leader in You", desc: "Helping young people understand that leadership is not simply a title. It begins with character, confidence, responsibility, influence, and how they show up for themselves and others." },
  { title: "The Beginning of What's Next", desc: "A youth and student adaptation of Sharon's signature message, encouraging young people to recognize what is possible and take intentional steps toward their next chapter." },
];

const YOUTH_AUDIENCES = [
  "Schools", "Student Organizations", "Youth Leadership Programs", "Youth Conferences",
  "Student Leadership Events", "College & University Students", "Emerging Leader Programs",
];

export default function Speaking() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 500);
    }
  }, [location.hash]);

  const scrollToInquiry = () => {
    document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Page data-testid="speaking-page">
      {/* HERO */}
      <section data-testid="speaking-hero" className="relative bg-obsidian text-ivory pt-40 pb-28 lg:pb-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.stage} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/60 to-obsidian" />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>Motivational Speaking · For the Room</Eyebrow>
            <h1 className="font-serif font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl mt-8 leading-[1.05]">
              <MaskedLine delay={0.2}>Inspire the Room.</MaskedLine>
              <MaskedLine delay={0.38} className="italic text-gold">Move People to Action.</MaskedLine>
            </h1>
            <Reveal delay={0.55}>
              <p className="text-base sm:text-lg font-light text-ivory/60 leading-relaxed mt-8 max-w-xl">
                Sharon speaks to executives, professionals, entrepreneurs, women,
                emerging leaders, college students, and youth, delivering messages that
                inspire audiences to recognize their potential, develop their
                leadership, and take intentional steps toward what's next.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold mt-6">
                For corporate, professional, university, women's leadership, and youth audiences.
              </p>
            </Reveal>
            <Reveal delay={0.65}>
              <div className="flex flex-wrap gap-4 mt-10">
                <button
                  onClick={scrollToInquiry}
                  data-testid="speaking-hero-cta-book"
                  className="bg-gold text-obsidian px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
                >
                  Book Sharon to Speak
                </button>
                <button
                  onClick={scrollToInquiry}
                  data-testid="speaking-hero-cta-youth"
                  className="border border-ivory/25 text-ivory px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  Inquire About Youth Speaking
                </button>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.3}>
              <div className="relative">
                <div className="absolute -inset-4 border border-gold/30 translate-x-4 translate-y-4 pointer-events-none" />
                <img
                  src={IMG.founderSpeak}
                  alt="Sharon Tanyi, Motivational Speaker"
                  data-testid="speaking-founder-photo"
                  className="relative w-full aspect-[4/5] object-cover object-top"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-obsidian/90 to-transparent p-6 pt-14 translate-x-4 translate-y-4">
                  <p className="font-serif text-lg">Sharon Tanyi</p>
                  <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold mt-1">
                    Executive Coach · Entrepreneur · Speaker
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROFESSIONAL TOPICS */}
      <section data-testid="topics-section" className="bg-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>Professional & Corporate Speaking</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-obsidian mt-8 max-w-2xl leading-tight">
              Messages that move <span className="italic text-gold-dark">people to action.</span>
            </h2>
          </Reveal>
          <div className="mt-16">
            {PRO_TOPICS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06}>
                <div
                  data-testid={`topic-${i}`}
                  className="group grid md:grid-cols-12 gap-3 md:gap-8 py-8 border-t border-obsidian/10 items-baseline hover:bg-cream/60 transition-colors duration-500 px-2 -mx-2"
                >
                  <span className="font-mono text-xs text-gold md:col-span-1">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-2xl lg:text-3xl text-obsidian md:col-span-6 group-hover:text-gold-dark transition-colors duration-300 leading-snug">
                    {t.title}
                  </h3>
                  <p className="text-sm sm:text-base font-light text-stone-text md:col-span-5">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO STRIP */}
      <section data-testid="speaking-gallery" className="bg-ivory pb-24 lg:pb-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid sm:grid-cols-3 gap-4">
          {[IMG.stageLights, IMG.audience, IMG.stage].map((src, i) => (
            <Reveal key={src} delay={i * 0.1}>
              <div className="overflow-hidden group">
                <img
                  src={src}
                  alt="Speaking engagement photography"
                  data-testid={`gallery-image-${i}`}
                  className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </Reveal>
          ))}
        </div>
        <p className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-stone-text">
          Event photography, updated with each engagement
        </p>
      </section>

      {/* YOUTH & STUDENT SPEAKING */}
      <section id="youth" data-testid="youth-speaking-section" className="bg-navy-deep text-ivory py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-6">
              <Reveal><Eyebrow>Youth & Student Speaking</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-8 leading-tight">
                  Purpose. Potential. Leadership. <span className="italic text-gold">Confidence.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-base sm:text-lg font-light text-ivory/60 leading-relaxed mt-8">
                  Sharon is passionate about encouraging and developing the next
                  generation of leaders.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="text-base font-light text-ivory/55 leading-relaxed mt-6">
                  Through engaging, age-appropriate messages, she inspires young people
                  to recognize their potential, build confidence, develop leadership
                  skills, discover their purpose, and take intentional steps toward
                  their future.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mt-10 mb-5">
                  Speaking experiences can be tailored for:
                </p>
                <div className="flex flex-wrap gap-2" data-testid="youth-audience-chips">
                  {YOUTH_AUDIENCES.map((a) => (
                    <span key={a} className="px-4 py-2 border border-white/15 font-mono text-[10px] tracking-[0.15em] uppercase text-ivory/60">
                      {a}
                    </span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.4}>
                <button
                  onClick={scrollToInquiry}
                  data-testid="youth-section-cta-inquire"
                  className="mt-12 bg-gold text-obsidian px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
                >
                  Inquire About Youth & Student Speaking
                </button>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <Reveal delay={0.15}>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-6">Featured Youth & Student Topics</p>
                <div className="border-t border-white/10">
                  {YOUTH_TOPICS.map((t, i) => (
                    <div key={t.title} className="py-7 border-b border-white/10" data-testid={`youth-speaking-topic-${i}`}>
                      <div className="flex items-baseline gap-6">
                        <span className="font-mono text-xs text-gold shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <p className="font-serif text-xl text-ivory/90">{t.title}</p>
                      </div>
                      <p className="text-sm font-light text-ivory/50 leading-relaxed mt-3 pl-10">{t.desc}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="font-serif italic text-lg text-gold/90 mt-10">
                  From the classroom to the boardroom.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SPEAKING INQUIRY FORM */}
      <section id="inquiry" data-testid="speaking-inquiry-section" className="bg-obsidian text-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>Speaking Inquiry</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-8 leading-tight">
                Bring the message <span className="italic text-gold">to your room.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base font-light text-ivory/55 leading-relaxed mt-6">
                Keynotes, workshops, and panels for corporate, professional,
                university, women's leadership, and youth audiences. Share a few
                details about your event and Sharon's team will follow up within 1 to
                2 business days.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.2}>
              <SpeakingInquiryForm />
            </Reveal>
          </div>
        </div>
      </section>
    </Page>
  );
}
